import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Langue (fr | en) et thème (light | dark), mémorisés dans le navigateur.
const PrefsContext = createContext(null);

const read = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* mode privé */ } };

function initialLang() {
  const saved = read('savantech_lang');
  if (saved === 'fr' || saved === 'en') return saved;
  return (navigator.language || 'fr').toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

function initialTheme() {
  // index.html pose déjà la classe avant le premier rendu (pas de flash) : on la relit
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function PrefsProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);
  const [theme, setThemeState] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0b1020' : '#FAFAF8');
  }, [theme]);

  const setLang = useCallback((l) => { setLangState(l); write('savantech_lang', l); }, []);
  const toggleTheme = useCallback(() => {
    setThemeState((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      write('savantech_theme', next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    lang,
    setLang,
    theme,
    toggleTheme,
    /** t('texte FR', 'English text') */
    t: (fr, en) => (lang === 'en' && en !== undefined ? en : fr),
    /** tx({ fr, en }) ou chaîne simple */
    tx: (v) => (v && typeof v === 'object' && 'fr' in v ? (lang === 'en' ? v.en ?? v.fr : v.fr) : v),
  }), [lang, setLang, theme, toggleTheme]);

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs() {
  return useContext(PrefsContext);
}
